"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import {
  Bold,
  Italic,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Undo,
  Redo,
  Link as LinkIcon,
  Image as ImageIcon,
  Table as TableIcon,
  Heading1,
  Heading2,
  Code,
  Search,
  GripVertical,
  Type,
  Hash,
  CalendarDays,
} from "lucide-react";

interface TemplateEditorWidgetProps {
  autoPlay?: boolean;
  className?: string;
}

type PlaceholderType = "string" | "number" | "date";

interface MockPlaceholder {
  name: string;
  label: string;
  type: PlaceholderType;
  required: boolean;
}

// Mock placeholders with human-readable Spanish labels
const mockPlaceholders: MockPlaceholder[] = [
  {
    name: "contributor_name",
    label: "Nombre del contribuyente",
    type: "string",
    required: true,
  },
  {
    name: "contribution_amount_text",
    label: "Cantidad aportada",
    type: "string",
    required: true,
  },
  {
    name: "contract_date",
    label: "Fecha de firma",
    type: "date",
    required: true,
  },
  {
    name: "fiscal_return_percentage",
    label: "Porcentaje fiscal",
    type: "number",
    required: true,
  },
];

// The placeholder that will be dragged
const DRAGGED_PLACEHOLDER = mockPlaceholders[1]; // Cantidad aportada

function ToolbarButton({
  icon: Icon,
  active = false,
}: {
  icon: React.ElementType;
  active?: boolean;
}) {
  return (
    <button
      className={`p-1.5 rounded hover:bg-zinc-200 transition-colors ${active ? "bg-zinc-300" : ""}`}
    >
      <Icon size={14} className="text-zinc-700" />
    </button>
  );
}

export function TemplateEditorWidget({ autoPlay = true, className }: TemplateEditorWidgetProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [animationPhase, setAnimationPhase] = useState<
    "initial" | "dragging" | "dropped" | "typing"
  >("initial");
  const [typedText, setTypedText] = useState("");
  const animationRef = useRef<boolean>(true);
  const positionRef = useRef({ x: 0, y: 0 });

  // The text that will be typed after the placeholder is inserted
  const continuationText =
    " que sera destinada a cubrir las necesidades de financiacion de la Obra.";

  useEffect(() => {
    if (!autoPlay) return;
    animationRef.current = true;

    const animateDrag = (targetX: number, targetY: number, duration: number) => {
      return new Promise<void>((resolve) => {
        const startX = positionRef.current.x;
        const startY = positionRef.current.y;
        const startTime = Date.now();

        const animate = () => {
          if (!animationRef.current) return resolve();

          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          // Smooth easing - cubic ease out
          const eased = 1 - Math.pow(1 - progress, 3);

          const newX = startX + (targetX - startX) * eased;
          const newY = startY + (targetY - startY) * eased;
          positionRef.current = { x: newX, y: newY };
          setDragPosition({ x: newX, y: newY });

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            resolve();
          }
        };

        requestAnimationFrame(animate);
      });
    };

    const typeText = async (text: string, charDelay: number) => {
      for (let i = 0; i <= text.length; i++) {
        if (!animationRef.current) return;
        setTypedText(text.slice(0, i));
        await delay(charDelay);
      }
    };

    const runSequence = async () => {
      // 1. Reset to initial state
      setIsDragging(false);
      setAnimationPhase("initial");
      setTypedText("");
      positionRef.current = { x: 0, y: 0 };
      setDragPosition({ x: 0, y: 0 });

      // 2. Pause to let the editor be seen (text ends mid-sentence)
      await delay(2000);
      if (!animationRef.current) return;

      // 3. Start drag - pick up the placeholder
      setIsDragging(true);
      setAnimationPhase("dragging");
      await delay(200);
      if (!animationRef.current) return;

      // 4. Lift the card slightly
      await animateDrag(-10, -10, 250);
      if (!animationRef.current) return;

      // 5. Animate drag to the insertion point in the document
      // Moving from the sidebar (right) to the end of the text
      await animateDrag(-300, 95, 700);
      if (!animationRef.current) return;

      // 6. Settle down
      await animateDrag(-300, 100, 150);
      if (!animationRef.current) return;

      // 7. Complete the drop - field appears at end of text
      setIsDragging(false);
      setAnimationPhase("dropped");
      await delay(400);
      if (!animationRef.current) return;

      // 8. Start typing the continuation text after the placeholder
      setAnimationPhase("typing");
      await delay(300);
      if (!animationRef.current) return;
      await typeText(continuationText, 35);
      if (!animationRef.current) return;

      // 9. Pause to appreciate the completed sentence
      await delay(3000);
      if (!animationRef.current) return;

      // 10. Loop
      if (animationRef.current) runSequence();
    };

    runSequence();
    return () => {
      animationRef.current = false;
    };
  }, [autoPlay]);

  // Get icon for placeholder type
  const getTypeIcon = (type: PlaceholderType) => {
    switch (type) {
      case "string":
        return <Type size={10} />;
      case "number":
        return <Hash size={10} />;
      case "date":
        return <CalendarDays size={10} />;
      default:
        return <Type size={10} />;
    }
  };

  // Render a placeholder tag (inline in document)
  const renderPlaceholderTag = (placeholder: MockPlaceholder) => (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[#f4f4f5] border border-[#e4e4e7] text-[#27272a] text-[11px] font-medium">
      <span>{placeholder.label}</span>
      {placeholder.required && (
        <span className="ml-0.5 px-1 py-0 rounded text-[8px] font-bold bg-[#27272a] text-white">
          REQ
        </span>
      )}
    </span>
  );

  // Render placeholder item in the palette
  const renderPaletteItem = (placeholder: MockPlaceholder, index: number) => {
    const isBeingDragged = isDragging && placeholder.name === DRAGGED_PLACEHOLDER.name;

    return (
      <motion.div
        key={placeholder.name}
        initial={{ opacity: 0, y: 8 }}
        animate={{
          opacity: isBeingDragged ? 0.4 : 1,
          y: 0,
          scale: isBeingDragged ? 0.98 : 1,
        }}
        transition={{ duration: 0.25, delay: index * 0.05 }}
        className={`
          group rounded-md border transition-all shadow-sm
          ${
            placeholder.required
              ? "border-zinc-300 bg-white hover:bg-zinc-100 hover:border-zinc-400"
              : "border-zinc-200 bg-white hover:bg-zinc-50 hover:border-zinc-300"
          }
        `}
      >
        <div className="flex items-center gap-1.5 px-2 py-1.5 cursor-grab">
          {/* Drag handle */}
          <GripVertical size={12} className="text-zinc-400 flex-shrink-0" />

          {/* Field label (human-readable) */}
          <span className="text-[10px] font-medium text-zinc-900 truncate flex-1">
            {placeholder.label}
          </span>

          {/* Badges */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Type badge */}
            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-[#f4f4f5] text-[#71717a]">
              {getTypeIcon(placeholder.type)}
            </span>
            {/* Required badge */}
            {placeholder.required && (
              <span className="inline-flex items-center px-1 py-0.5 rounded text-[8px] font-bold bg-[#27272a] text-white">
                REQ
              </span>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  // Render the floating dragged placeholder
  const renderDraggedPlaceholder = () => {
    if (!isDragging) return null;

    return (
      <motion.div
        className="absolute z-50 pointer-events-none"
        initial={{ scale: 1, rotate: 0, opacity: 0 }}
        animate={{
          scale: 1.08,
          rotate: -3,
          opacity: 1,
          x: dragPosition.x,
          y: dragPosition.y,
        }}
        transition={{
          x: { duration: 0 },
          y: { duration: 0 },
          scale: { duration: 0.2 },
          rotate: { duration: 0.2 },
          opacity: { duration: 0.15 },
        }}
        style={{
          // Start position - at the Cantidad aportada item in the palette
          right: "12px",
          top: "132px",
        }}
      >
        <div
          className="px-2.5 py-1.5 rounded-md border border-zinc-400 bg-white"
          style={{
            boxShadow: "0 12px 30px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div className="flex items-center gap-1.5">
            <GripVertical size={12} className="text-zinc-500" />
            <span className="text-[10px] font-medium text-zinc-900">
              {DRAGGED_PLACEHOLDER.label}
            </span>
            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-[#f4f4f5] text-[#71717a]">
              {getTypeIcon(DRAGGED_PLACEHOLDER.type)}
            </span>
            <span className="inline-flex items-center px-1 py-0.5 rounded text-[8px] font-bold bg-[#27272a] text-white">
              REQ
            </span>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className={className}>
      <div
        className="relative mx-auto"
        style={{
          maxWidth: "720px",
          perspective: "1200px",
        }}
      >
        <motion.div
          initial={{ opacity: 0, rotateX: 3 }}
          animate={{ opacity: 1, rotateX: 0 }}
          transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          style={{
            transformStyle: "preserve-3d",
          }}
        >
          {/* Main layout: Editor + Palette sidebar - integrated flush */}
          <div
            className="grid grid-cols-[1fr_180px] bg-white rounded-lg border border-[#e4e4e7] overflow-hidden"
            style={{
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04)",
            }}
          >
            {/* Main Editor Panel */}
            <div className="overflow-hidden flex flex-col">
              {/* Toolbar - matches TemplateEditor.tsx exactly */}
              <div className="bg-zinc-50 px-3 py-1.5 flex flex-wrap gap-0.5 h-[41px] items-center">
                {/* Text Formatting */}
                <div className="flex gap-0.5 border-r border-zinc-300 pr-1.5 mr-1.5">
                  <ToolbarButton icon={Bold} />
                  <ToolbarButton icon={Italic} />
                  <ToolbarButton icon={Strikethrough} />
                </div>

                {/* Headings */}
                <div className="flex gap-0.5 border-r border-zinc-300 pr-1.5 mr-1.5">
                  <ToolbarButton icon={Heading1} />
                  <ToolbarButton icon={Heading2} />
                </div>

                {/* Alignment */}
                <div className="flex gap-0.5 border-r border-zinc-300 pr-1.5 mr-1.5">
                  <ToolbarButton icon={AlignLeft} active />
                  <ToolbarButton icon={AlignCenter} />
                  <ToolbarButton icon={AlignRight} />
                  <ToolbarButton icon={AlignJustify} />
                </div>

                {/* Lists */}
                <div className="flex gap-0.5 border-r border-zinc-300 pr-1.5 mr-1.5">
                  <ToolbarButton icon={List} />
                  <ToolbarButton icon={ListOrdered} />
                </div>

                {/* Insert Elements */}
                <div className="flex gap-0.5 border-r border-zinc-300 pr-1.5 mr-1.5">
                  <ToolbarButton icon={LinkIcon} />
                  <ToolbarButton icon={ImageIcon} />
                  <ToolbarButton icon={TableIcon} />
                  <ToolbarButton icon={Code} />
                </div>

                {/* Undo/Redo */}
                <div className="flex gap-0.5">
                  <ToolbarButton icon={Undo} />
                  <ToolbarButton icon={Redo} />
                </div>
              </div>

              {/* Horizontal divider line spanning full width */}
              <div className="border-b border-zinc-200" />

              {/* Editor Content Area - fixed height to prevent resize during typing animation */}
              <div className="p-4 h-[400px] bg-white overflow-hidden">
                {/* Document content - simplified financing contract excerpt */}
                <div className="prose prose-sm max-w-none text-justify">
                  {/* Title */}
                  <h2 className="text-center text-base font-bold underline mb-4 text-zinc-900">
                    CONTRATO DE FINANCIACION
                  </h2>

                  {/* Date and location */}
                  <p className="text-right text-[11px] text-zinc-700 mb-4">
                    En{" "}
                    {renderPlaceholderTag({
                      name: "organization_city",
                      label: "Ciudad",
                      type: "string",
                      required: true,
                    })}
                    , a{" "}
                    {renderPlaceholderTag({
                      name: "contract_date",
                      label: "Fecha de firma",
                      type: "date",
                      required: true,
                    })}
                  </p>

                  {/* REUNIDOS section */}
                  <h3 className="text-center text-xs font-bold underline mb-3 text-zinc-900">
                    REUNIDOS
                  </h3>

                  {/* Party info */}
                  <p className="text-[11px] text-zinc-700 mb-3 leading-relaxed">
                    <span className="font-bold underline">De una parte,</span>
                    <br />
                    D.{" "}
                    {renderPlaceholderTag({
                      name: "producer_admin_name",
                      label: "Nombre del administrador",
                      type: "string",
                      required: true,
                    })}
                    , con DNI{" "}
                    {renderPlaceholderTag({
                      name: "producer_admin_nif",
                      label: "NIF administrador",
                      type: "string",
                      required: true,
                    })}
                    , en calidad de representante legal de la entidad{" "}
                    <strong>
                      {renderPlaceholderTag({
                        name: "producer_company_name",
                        label: "Nombre de la productora",
                        type: "string",
                        required: true,
                      })}
                    </strong>
                    .
                  </p>

                  <p className="text-[11px] text-zinc-700 mb-3 leading-relaxed">
                    <span className="font-bold underline">De otra parte,</span>
                    <br />
                    {renderPlaceholderTag({
                      name: "contributor_name",
                      label: "Nombre del contribuyente",
                      type: "string",
                      required: true,
                    })}
                    , con DNI{" "}
                    {renderPlaceholderTag({
                      name: "contributor_nif",
                      label: "NIF contribuyente",
                      type: "string",
                      required: true,
                    })}
                    , y domicilio en{" "}
                    {renderPlaceholderTag({
                      name: "contributor_address",
                      label: "Domicilio",
                      type: "string",
                      required: true,
                    })}
                    . En adelante, el <strong>"Contribuyente o inversor"</strong>.
                  </p>

                  {/* PRIMERA clause - where the insertion happens */}
                  <h3 className="text-xs font-bold underline mb-2 text-zinc-900 mt-4">
                    PRIMERA. - OBJETO
                  </h3>

                  <p className="text-[11px] text-zinc-700 leading-relaxed">
                    {/* Text ends mid-sentence, waiting for the field to be dropped */}
                    1.2. El Contribuyente se compromete a participar aportando una cantidad de
                    {/* The placeholder appears here after drag-drop */}
                    <AnimatePresence>
                      {(animationPhase === "dropped" || animationPhase === "typing") && (
                        <motion.span
                          key="inserted"
                          initial={{
                            scale: 0.8,
                            opacity: 0,
                            backgroundColor: "rgba(108, 71, 255, 0.3)",
                          }}
                          animate={{
                            scale: 1,
                            opacity: 1,
                            backgroundColor: "rgba(244, 244, 245, 1)",
                          }}
                          transition={{ duration: 0.3, backgroundColor: { duration: 0.6 } }}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 mx-1 rounded border border-[#e4e4e7] text-[#27272a] text-[11px] font-medium"
                        >
                          <span>Cantidad aportada</span>
                          <span className="ml-0.5 px-1 py-0 rounded text-[8px] font-bold bg-[#27272a] text-white">
                            REQ
                          </span>
                        </motion.span>
                      )}
                    </AnimatePresence>
                    {/* Blinking cursor when in initial/dragging state or typing */}
                    {(animationPhase === "initial" || animationPhase === "dragging") && (
                      <motion.span
                        animate={{ opacity: [1, 0] }}
                        transition={{ duration: 0.8, repeat: Infinity, repeatType: "reverse" }}
                        className="inline-block w-[2px] h-[14px] bg-zinc-900 align-middle ml-0.5"
                      />
                    )}
                    {/* Continuation text types in after the field is dropped */}
                    {animationPhase === "typing" && (
                      <>
                        <span>{typedText}</span>
                        <motion.span
                          animate={{ opacity: [1, 0] }}
                          transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                          className="inline-block w-[2px] h-[14px] bg-zinc-900 align-middle"
                        />
                      </>
                    )}
                    {/* Show full text at rest after typing completes */}
                    {animationPhase === "dropped" && typedText === "" && null}
                  </p>
                </div>
              </div>
            </div>

            {/* Placeholder Palette Sidebar - integrated flush with editor */}
            <div className="bg-zinc-50 border-l border-zinc-200 overflow-hidden flex flex-col">
              {/* Header - height matches toolbar for aligned divider line */}
              <div className="px-3 h-[41px] flex items-center justify-between">
                <h3 className="text-[11px] font-semibold text-zinc-900">Placeholders</h3>
                <span className="text-[10px] text-zinc-500">{mockPlaceholders.length}</span>
              </div>

              {/* Horizontal divider - aligns with editor divider */}
              <div className="border-b border-zinc-200" />

              {/* Search */}
              <div className="px-2 pt-2 pb-1">
                <div className="relative">
                  <Search
                    size={12}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400"
                  />
                  <input
                    type="text"
                    placeholder="Buscar..."
                    className="w-full pl-7 pr-2 py-1 text-[10px] border border-zinc-200 rounded focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-white"
                    readOnly
                    autoComplete="off"
                    tabIndex={-1}
                  />
                </div>
              </div>

              {/* Placeholder list */}
              <div className="px-2 pb-2 space-y-1.5 flex-1">
                {mockPlaceholders.map((placeholder, index) =>
                  renderPaletteItem(placeholder, index),
                )}
              </div>

              {/* Drag hint - no border, flows naturally from placeholder list */}
              <div className="px-3 py-2 bg-zinc-50">
                <p className="text-[9px] text-zinc-500 text-center">
                  Arrastra los campos al documento
                </p>
              </div>
            </div>
          </div>

          {/* Floating dragged placeholder */}
          {renderDraggedPlaceholder()}
        </motion.div>

        {/* Subtle drop zone indicator during drag */}
        <AnimatePresence>
          {isDragging && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at 30% 60%, rgba(108, 71, 255, 0.03) 0%, transparent 50%)",
              }}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export default TemplateEditorWidget;
